// Service de calcul d'importance des médias
import { LazyLoadLogger } from './LazyLoadLogger';

export type MediaType = 'image' | 'video';

export interface MediaImportanceWeights {
    visibility: number;  // Poids de la visibilité (0-1)
    size: number;        // Poids de la taille (0-1)
    position: number;    // Poids de la position (0-1)
    type: number;        // Poids du type (0-1)
}

export interface MediaImportanceConfig {
    weights: MediaImportanceWeights;
    videoBonus: number;  // Bonus pour les vidéos (défaut: 1.2)
}

export interface ViewportInfo {
    top: number;
    bottom: number;
    left: number;
    right: number;
    centerY: number;
    centerX: number;
    width: number;
    height: number;
}

export interface MediaImportanceResult {
    score: number;           // Score total (0-100)
    visibilityScore: number; // Score de visibilité (0-100)
    sizeScore: number;       // Score de taille (0-100)
    positionScore: number;   // Score de position (0-100)
    typeBonus: number;       // Bonus de type (1.0 ou videoBonus)
}

class MediaImportanceServiceClass {
    private config: MediaImportanceConfig = {
        weights: {
            visibility: 0.4,  // 40% poids
            size: 0.3,        // 30% poids
            position: 0.2,    // 20% poids
            type: 0.1         // 10% poids
        },
        videoBonus: 1.2
    };

    /**
     * Configure le service
     */
    configure(config: Partial<MediaImportanceConfig>) {
        if (config.weights) {
            this.config.weights = { ...this.config.weights, ...config.weights };
        }
        if (config.videoBonus !== undefined) {
            this.config.videoBonus = config.videoBonus;
        }
        LazyLoadLogger.logMedia('Configuration du service d\'importance', this.config);
    }

    /**
     * Calcule le score d'importance d'un média
     * 
     * @param element L'élément HTML du média
     * @param viewport Info du viewport
     * @param mediaType Type de média
     * @returns Score d'importance (0-100)
     */
    calculateImportance(
        element: HTMLElement,
        viewport: ViewportInfo,
        mediaType: MediaType
    ): MediaImportanceResult {
        const rect = element.getBoundingClientRect();

        // 1. Score de visibilité
        const visibilityScore = this.calculateVisibilityScore(rect, viewport);

        // 2. Score de taille
        const sizeScore = this.calculateSizeScore(rect, viewport);

        // 3. Score de position (distance du centre)
        const positionScore = this.calculatePositionScore(rect, viewport);

        // 4. Bonus de type
        const typeBonus = mediaType === 'video' ? this.config.videoBonus : 1.0;

        // Score total avec pondération
        const { weights } = this.config;
        const baseScore = 
            (visibilityScore * weights.visibility) +
            (sizeScore * weights.size) +
            (positionScore * weights.position) +
            (typeBonus * 10 * weights.type); // typeBonus normalisé sur 10

        const finalScore = Math.min(100, baseScore * typeBonus);

        return {
            score: Math.round(finalScore),
            visibilityScore: Math.round(visibilityScore),
            sizeScore: Math.round(sizeScore),
            positionScore: Math.round(positionScore),
            typeBonus
        };
    }

    /**
     * Calcule le score de visibilité (0-100)
     * - Visible dans viewport = 100
     * - Proche du viewport = 50-99
     * - Loin du viewport = 0-49
     */
    private calculateVisibilityScore(rect: DOMRect, viewport: ViewportInfo): number {
        // Element complètement visible
        if (
            rect.top >= viewport.top &&
            rect.bottom <= viewport.bottom &&
            rect.left >= viewport.left &&
            rect.right <= viewport.right
        ) {
            return 100;
        }

        // Element partiellement visible
        const isIntersecting = !(
            rect.bottom < viewport.top ||
            rect.top > viewport.bottom ||
            rect.right < viewport.left ||
            rect.left > viewport.right
        );

        if (isIntersecting) {
            // Calculer le pourcentage de surface visible
            const visibleTop = Math.max(rect.top, viewport.top);
            const visibleBottom = Math.min(rect.bottom, viewport.bottom);
            const visibleLeft = Math.max(rect.left, viewport.left);
            const visibleRight = Math.min(rect.right, viewport.right);

            const visibleArea = (visibleBottom - visibleTop) * (visibleRight - visibleLeft);
            const totalArea = rect.width * rect.height;

            const visibleRatio = totalArea > 0 ? visibleArea / totalArea : 0;
            return 70 + (visibleRatio * 30); // 70-100 pour partiellement visible
        }

        // Element hors viewport - calculer la distance
        let distance = 0;
        if (rect.bottom < viewport.top) {
            distance = viewport.top - rect.bottom;
        } else if (rect.top > viewport.bottom) {
            distance = rect.top - viewport.bottom;
        }

        // Score décroissant avec la distance (max 2000px)
        const maxDistance = 2000;
        const distanceRatio = Math.min(distance / maxDistance, 1);
        return (1 - distanceRatio) * 70; // 0-70 pour hors viewport
    }

    /**
     * Calcule le score de taille (0-100)
     * Plus grand = plus important
     */
    private calculateSizeScore(rect: DOMRect, viewport: ViewportInfo): number {
        const area = rect.width * rect.height;
        const viewportArea = viewport.width * viewport.height;

        // Pourcentage de l'écran occupé
        const areaRatio = Math.min(area / viewportArea, 1);

        // Score linéaire basé sur la taille
        return areaRatio * 100;
    }

    /**
     * Calcule le score de position (0-100)
     * Plus proche du centre = plus important
     */
    private calculatePositionScore(rect: DOMRect, viewport: ViewportInfo): number {
        const elementCenterX = rect.left + rect.width / 2;
        const elementCenterY = rect.top + rect.height / 2;

        // Distance du centre du viewport
        const deltaX = Math.abs(elementCenterX - viewport.centerX);
        const deltaY = Math.abs(elementCenterY - viewport.centerY);
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // Distance maximale (coin de l'écran)
        const maxDistance = Math.sqrt(
            (viewport.width / 2) * (viewport.width / 2) +
            (viewport.height / 2) * (viewport.height / 2)
        );

        // Score décroissant avec la distance
        const distanceRatio = Math.min(distance / maxDistance, 1);
        return (1 - distanceRatio) * 100;
    }

    /**
     * Récupère les informations du viewport
     */
    getViewportInfo(): ViewportInfo {
        const width = window.innerWidth;
        const height = window.innerHeight;

        return {
            top: 0,
            bottom: height,
            left: 0,
            right: width,
            centerX: width / 2,
            centerY: height / 2,
            width,
            height
        };
    }

    /**
     * Log le score d'un média
     */
    logImportance(mediaId: string, result: MediaImportanceResult) {
        LazyLoadLogger.logMedia(`Score d'importance pour ${mediaId}: ${result.score}`, {
            score: result.score,
            visibility: result.visibilityScore,
            size: result.sizeScore,
            position: result.positionScore,
            typeBonus: result.typeBonus
        });
    }

    /**
     * Récupère la configuration actuelle
     */
    getConfig(): MediaImportanceConfig {
        return { ...this.config, weights: { ...this.config.weights } };
    }
}

// Export singleton
export const MediaImportanceService = new MediaImportanceServiceClass();



